import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  FormControl,
  Button,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Box,
  Typography,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../../utils/swalConfig';
import { useNavigate } from 'react-router-dom';

// Project import
import { gridSpacing } from 'config.js';

const ClientPage = () => {
  const [data, setData] = useState([]);
  const [buData, setBuData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [buFilter, setBuFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  // API Functions
  const fetchBusinessUnits = async () => {
    try {
      const response = await fetch('https://apidashboard.eurekagroup.id/bu', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      setBuData(result);
      console.log('Business units loaded:', result);
    } catch (error) {
      console.error('Error fetching business units:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Warning!',
        text: 'Gagal memuat data business unit'
      });
    }
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://api-sso-v2.eurekagroup.id/api/clients', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      const mappedData = (result.data || result).map(client => ({
        ...client,
        id: client._id
      }));
      setData(mappedData);
      console.log('Clients loaded:', result);
    } catch (error) {
      console.error('Error fetching clients:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: `Gagal memuat data client: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteClient = async (id) => {
    try {
      const response = await fetch(`https://api-sso-v2.eurekagroup.id/api/clients/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Remove from local state
      setData(prevData => prevData.filter(client => client.id !== id && client._id !== id));
      
      SwalConfig.success(
        'Berhasil Dihapus!',
        'Data client telah berhasil dihapus dari sistem.',
        2000
      );
    } catch (error) {
      console.error('Error deleting client:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: `Gagal menghapus client: ${error.message}`
      });
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchBusinessUnits();
    fetchClients();
  }, []);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleRoleFilterChange = (event) => {
    setRoleFilter(event.target.value);
  };

  const handleBuFilterChange = (event) => {
    setBuFilter(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (id) => {
    SwalConfig.deleteConfirm('client ini').then((result) => {
      if (result.isConfirmed) {
        deleteClient(id);
      }
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'primary';
      case 'superadmin':
        return 'error';
      case 'user':
        return 'success';
      default:
        return 'default';
    }
  };

  const getBuName = (idBu) => {
    const bu = buData.find(item => item.bu_code === idBu.toString());
    return bu ? bu.bu_name : `BU ${idBu}`;
  };

  const filteredData = (data || [])
    .filter(client => {
      if (!client) return false;
      const clientName = (client.client || '').toLowerCase();
      const role = (client.role || '').toLowerCase();
      const buName = (getBuName(client.id_bu) || '').toLowerCase();
      const search = (searchText || '').toLowerCase();

      const matchesSearch = clientName.includes(search) ||
                           role.includes(search) ||
                           buName.includes(search);
      const matchesRole = roleFilter === '' || client.role === roleFilter;
      const matchesBu = buFilter === '' || (client.id_bu != null && client.id_bu.toString() === buFilter);
      
      return matchesSearch && matchesRole && matchesBu;
    })
    .sort((a, b) => (a?.client || '').localeCompare(b?.client || ''));

  const uniqueRoles = [...new Set((data || []).map(client => client?.role).filter(Boolean))];

  return (
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <BusinessIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Manajemen Client SSO
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Kelola data client SSO dengan mudah dan efisien
                </Typography>
              </Box>
            </Box>
          }
        action={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              color="primary" 
              startIcon={<RefreshIcon />} 
              onClick={fetchClients}
              disabled={loading}
              sx={{ 
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {loading ? <CircularProgress size={20} /> : 'Refresh'}
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />} 
              onClick={() => navigate('/sso/client/add')}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)'
                }
              }}
            >
              ✨ Tambah Client
            </Button>
          </Box>
        }
        sx={{ pb: 2 }}
      />
      <Divider />
      <CardContent>
        {/* Filter Section */}
        <Box sx={{ 
          mb: 3, 
          p: 3, 
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200'
        }} className="bg-gradient-to-r from-gray-50 to-blue-50 border-gray-200 shadow-sm animate-slide-up">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Cari Client"
                variant="outlined"
                value={searchText}
                onChange={handleSearchChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.background.paper
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <Select
                  value={roleFilter}
                  onChange={handleRoleFilterChange}
                  displayEmpty
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterIcon color="action" />
                    </InputAdornment>
                  }
                  sx={{
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.background.paper
                  }}
                >
                  <MenuItem value="">
                    <em>Semua Role</em>
                  </MenuItem>
                  {uniqueRoles.map(role => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <Select
                  value={buFilter}
                  onChange={handleBuFilterChange}
                  displayEmpty
                  sx={{
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.background.paper
                  }}
                >
                  <MenuItem value="">
                    <em>Semua Business Unit</em>
                  </MenuItem>
                  {buData.map(bu => (
                    <MenuItem key={bu._id} value={bu.bu_code}>
                      {bu.bu_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {filteredData.length}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Total Client
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Table Section */}
        <Box sx={{ 
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
          overflow: 'hidden'
        }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: 400,
              flexDirection: 'column',
              gap: 2
            }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                Memuat data client...
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}>
                    No
                  </TableCell>
                  <TableCell sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    minWidth: 200
                  }}>
                    Client
                  </TableCell>
                  <TableCell sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}>
                    Business Unit
                  </TableCell>
                  <TableCell sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}>
                    Role
                  </TableCell>
                  <TableCell sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    textAlign: 'center'
                  }}>
                    Aksi
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((client, index) => (
                  <TableRow 
                    key={client.id}
                    sx={{ 
                      '&:hover': { 
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover',
                        transform: 'scale(1.001)',
                        transition: 'all 0.2s ease'
                      },
                      '&:nth-of-type(even)': {
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'action.hover'
                      }
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>
                      {index + 1 + page * rowsPerPage}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar 
                          sx={{ 
                            width: 40, 
                            height: 40,
                            bgcolor: 'primary.main'
                          }}
                        >
                          <BusinessIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {client.client}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                            ID: {client.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getBuName(client.id_bu)}
                        variant="outlined"
                        color="info"
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={client.role}
                        color={getRoleColor(client.role)}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <IconButton 
                          onClick={() => navigate(`/sso/client/view/${client.id}`)} 
                          sx={{ 
                            color: 'success.main',
                            '&:hover': { 
                              bgcolor: 'success.light',
                              color: 'white',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                          size="small"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          onClick={() => navigate(`/sso/client/edit/${client.id}`)} 
                          sx={{ 
                            color: 'info.main',
                            '&:hover': { 
                              bgcolor: 'info.light',
                              color: 'white',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleDeleteClick(client.id)} 
                          sx={{ 
                            color: 'error.main',
                            '&:hover': { 
                              bgcolor: 'error.light',
                              color: 'white',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'grey.200',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover'
                }}
              />
            </>
          )}
        </Box>
      </CardContent>
    </Card>
    </div>
  );
};

export default ClientPage;
